import type { ServerResponse } from 'node:http';
import { CookieKey } from '@/constants';
import type { NextApiResponse } from 'next';
import parseCookie from './parseCookie.utils';

export class ServerCookieManager {
  private res: NextApiResponse | ServerResponse | undefined;
  private cookieString = '';
  private cookieHeaders: string[] = [];

  constructor(cookieString = '', res?: NextApiResponse | ServerResponse) {
    this.cookieString = cookieString;
    this.res = res;
    if (res?.getHeader('Set-Cookie')) {
      const existing = res.getHeader('Set-Cookie');
      this.cookieHeaders = Array.isArray(existing) ? existing : [existing as string];
    }
  }

  /**
   * Get a cookie value by key
   * @param key - The cookie key to get
   * @returns The cookie value or undefined if not found
   */
  public getCookie(key: CookieKey): string | undefined {
    const cookies = parseCookie(this.cookieString);
    return cookies[key];
  }

  /**
   * Get all cookies as an object
   * @returns Object containing all cookies
   */
  public getAllCookies(): Record<string, string> {
    return parseCookie(this.cookieString);
  }

  /**
   * Set a cookie with the given key and value
   * @param key - The cookie key
   * @param value - The cookie value
   * @param options - Cookie options (path, expires, etc.)
   */
  public setCookie(
    key: CookieKey,
    value: string,
    options: {
      path?: string;
      expires?: Date | string;
      maxAge?: number;
      domain?: string;
      secure?: boolean;
      httpOnly?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {},
  ): void {
    if (!this.res) {
      throw new Error('Response object is required for setting cookies');
    }

    const defaultOptions = {
      path: '/',
      secure: true,
      sameSite: 'Lax' as const,
    };

    const mergedOptions = { ...defaultOptions, ...options };
    let cookieValue = `${key}=${value}`;

    if (mergedOptions.path) cookieValue += `; Path=${mergedOptions.path}`;
    if (mergedOptions.expires) cookieValue += `; Expires=${mergedOptions.expires}`;
    if (mergedOptions.maxAge) cookieValue += `; Max-Age=${mergedOptions.maxAge}`;
    if (mergedOptions.domain) cookieValue += `; Domain=${mergedOptions.domain}`;
    if (mergedOptions.secure) cookieValue += '; Secure';
    if (mergedOptions.httpOnly) cookieValue += '; HttpOnly';
    if (mergedOptions.sameSite) cookieValue += `; SameSite=${mergedOptions.sameSite}`;

    this.cookieHeaders.push(cookieValue);
    this.res.setHeader('Set-Cookie', this.cookieHeaders);
  }

  /**
   * Remove a cookie by key
   * @param key - The cookie key to remove
   */
  public removeCookie(key: CookieKey): void {
    if (!this.res) {
      throw new Error('Response object is required for removing cookies');
    }

    this.setCookie(key, '', {
      expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
      httpOnly: true,
      secure: true,
      sameSite: 'Strict',
    });
  }

  /**
   * Remove multiple cookies by keys
   * @param keys - Array of cookie keys to remove
   */
  public removeCookies(keys: CookieKey[]): void {
    keys.forEach((key) => this.removeCookie(key));
  }

  /**
   * Remove all user-related cookies (useful for logout)
   */
  public removeUserCookies(): void {
    const userRelatedCookies = [
      CookieKey.UserId,
      CookieKey.AccessToken,
      CookieKey.RefreshToken,
      CookieKey.SyncingESBannerExpiredTime,
      CookieKey.BrowserUuid,
      CookieKey.UserEmail,
      CookieKey.NewBrowserUuid,
      CookieKey.VerificationStatus,
      CookieKey.PmtVerificationStatus,
      CookieKey.IsValidVerification,
      CookieKey.HasGrantedAllAccess,
      CookieKey.FirebaseMessageToken,
    ];

    this.removeCookies(userRelatedCookies);
  }
}
